// src/components/chat/feedback/FeedbackModal.tsx

import * as React from "react";
import { FeedbackModalProps } from "../../../interfaces/feedbackTypes";
import ThankYouIcon from "../../../../assets/ThankYouFeedback.svg";
import { createLogger } from "../../../utils/logger";
import { feedbackModalStyles } from "./feedbackStyles";

const log = createLogger("FeedbackModal");

/**
 * FeedbackModal component displays a modal for users to provide detailed feedback
 * Shows after clicking thumbs up/down buttons
 */
const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  rating,
  messageId,
  onClose,
  onSubmit,
}) => {
  const [comment, setComment] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showThankYou, setShowThankYou] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isTextareaFocused, setIsTextareaFocused] = React.useState(false);
  const [isButtonHovered, setIsButtonHovered] = React.useState(false);

  // Store timer references for cleanup
  const autoCloseTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const resetTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setComment("");
      setShowThankYou(false);
      setIsSubmitting(false);
      setError(null);
    } else {
      // Clear error when modal opens
      setError(null);
    }
  }, [isOpen]);

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setIsSubmitting(true);
    log.info("Submitting feedback from modal", { messageId, rating, hasComment: !!comment });

    try {
      await onSubmit(comment);

      // Reset submitting state and show thank you message
      setIsSubmitting(false);
      setShowThankYou(true);

      // Clear any existing timers before setting new ones
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }

      // Auto-close after showing thank you
      autoCloseTimerRef.current = setTimeout(() => {
        onClose();
        // Reset state after closing
        resetTimerRef.current = setTimeout(() => {
          setShowThankYou(false);
          setComment("");
        }, 300);
      }, 2000);
    } catch (error) {
      log.error("Failed to submit feedback from modal", {
        messageId,
        error: error instanceof Error ? error.message : String(error),
      });
      setIsSubmitting(false);
      // Show error to user
      setError(error instanceof Error ? error.message : "Failed to submit feedback");
    }
  };

  /**
   * Get placeholder text based on rating
   */
  const getPlaceholderText = (): string => {
    if (rating === "thumbs_up") {
      return "What did you like most about this response?";
    } else if (rating === "thumbs_down") {
      return "What was wrong? Please be specific about the issue.";
    }
    return "Share your feedback...";
  };

  // Prevent clicks on modal content from closing the modal
  const handleModalContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div style={feedbackModalStyles.overlay} onClick={!isSubmitting ? onClose : undefined}>
      <div style={feedbackModalStyles.modal} onClick={handleModalContentClick}>
        {!showThankYou ? (
          <>
            {/* Header */}
            <h2 style={feedbackModalStyles.header}>Feedback</h2>

            {/* Close button */}
            <button
              onClick={onClose}
              disabled={isSubmitting}
              style={feedbackModalStyles.closeButton(isSubmitting)}
              aria-label="Close feedback modal"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#666666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Feedback textarea */}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={getPlaceholderText()}
              disabled={isSubmitting}
              style={feedbackModalStyles.textarea(isSubmitting, isTextareaFocused)}
              onFocus={() => setIsTextareaFocused(true)}
              onBlur={() => setIsTextareaFocused(false)}
            />

            {/* Placeholder style for light gray color */}
            <style>{`
              textarea::placeholder {
                color: #999999;
              }
            `}</style>

            {/* Error message */}
            {error && <div style={feedbackModalStyles.errorMessage}>{error}</div>}

            {/* Submit button */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={feedbackModalStyles.submitButton(isSubmitting, isButtonHovered)}
              onMouseEnter={() => !isSubmitting && setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </button>
          </>
        ) : (
          // Thank you state
          <div style={feedbackModalStyles.thankYouContainer}>
            <ThankYouIcon style={feedbackModalStyles.thankYouIcon} />

            <h2 style={feedbackModalStyles.thankYouTitle}>Thank you</h2>

            <p style={feedbackModalStyles.thankYouText}>
              The feedback helps us improve.
              <br />
              We appreciate the time you took to send
              <br />
              us the feedback.
            </p>
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default FeedbackModal;
